import { ListEntry, PhotoEntry, RecordingEntry } from '@/components/explore/ExploreEntry';
import { ExploreConversationEntry } from '@/components/explore/ExploreConversationEntry';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ItemSelector } from '@/components/widget/item-selector';
import { useNavigateWithContentFlush } from '@/hooks';
import {
  useAddContentAttachment,
  useContentAttachments,
  useRemoveContentAttachment,
} from '@/hooks/useContentAttachmentQueries';
import { useContentList } from '@/hooks/useContentQueries';
import {
  useContentLinks,
  useCreateContentLink,
  useDeleteContentLink,
} from '@/hooks/useContentLinkQueries';
import { getContentDisplayLabel } from '@/lib/content-labels';
import { useSelectedItemStore } from '@/store/selectedItemStore';
import { appendBreadcrumbStep, normalizeBreadcrumbTrail } from '@/utils/breadcrumb-state';
import { toContentHref } from '@/utils/content-ref';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { Content, ContentType } from '@vivport/backend/types';
import { Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface ContentAttachmentsProps {
  parentContentId: number;
  title: string;
  emptyText: string;
  breadcrumbLabel: string;
  /**
   * `embedded`: no section heading or extra vertical margin — same list rows as `section`,
   * for use inside message threads and other nested contexts. Management affordances only
   * render in `section` layout.
   */
  layout?: 'section' | 'embedded';
}

interface RemoveTarget {
  kind: 'attachment' | 'link';
  contentId: number;
  label: string;
}

function toSelectorItems(items: readonly { id: number; title?: string | null }[]) {
  return items.map((item) => ({
    id: item.id,
    title: typeof item.title === 'string' ? item.title : null,
  }));
}

export function ContentAttachments({
  parentContentId,
  title,
  emptyText,
  breadcrumbLabel,
  layout = 'section',
}: ContentAttachmentsProps) {
  const { data, isLoading } = useContentAttachments(parentContentId);
  const navigate = useNavigateWithContentFlush();
  const location = useLocation();
  const from = normalizeBreadcrumbTrail(location.state?.from);
  const { toggleItem, isItemSelected, clearSelectedItems } = useSelectedItemStore();

  const isEmbedded = layout === 'embedded';
  const manageable = !isEmbedded && parentContentId > 0;

  const linksQuery = useContentLinks(manageable ? parentContentId : 0);
  const addAttachment = useAddContentAttachment();
  const removeAttachment = useRemoveContentAttachment();
  const createLink = useCreateContentLink();
  const deleteLink = useDeleteContentLink();

  const [attachPickerOpen, setAttachPickerOpen] = useState(false);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [attachSelection, setAttachSelection] = useState<number | null>(null);
  const [linkSelection, setLinkSelection] = useState<number | null>(null);
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const pickerContent = useContentList(
    attachPickerOpen || linkPickerOpen ? { limit: 100 } : undefined
  );

  const items = useMemo(
    () =>
      (data?.items ?? [])
        // MessageExtracts surface in their own panel on the source, never as attachments.
        .filter((item) => item.contentType !== ContentType.MESSAGE_EXTRACT)
        .sort((a, b) => new Date(a.timelineAt).getTime() - new Date(b.timelineAt).getTime()),
    [data?.items]
  );

  // Attached messages are thread roots, so each is rendered as the conversation it
  // stands for — the same row Explore shows — keyed by the root's content id.
  const conversationByMessageId = useMemo(
    () => new Map((data?.messageConversations ?? []).map((c) => [c.representativeMessageId, c])),
    [data?.messageConversations]
  );

  const outgoingLinks = linksQuery.data?.outgoing ?? [];
  const incomingLinks = linksQuery.data?.incoming ?? [];

  const navigateToAttachment = (contentRef: string, attachment: Content) => {
    const currentHref = `${location.pathname}${location.search}${location.hash}`;
    const destinationHref = toContentHref(contentRef);
    const destinationLabel = getContentDisplayLabel(attachment);
    const trailWithParent = appendBreadcrumbStep(from, { label: breadcrumbLabel, href: currentHref });
    const nextFrom = appendBreadcrumbStep(trailWithParent, {
      label: destinationLabel,
      href: destinationHref,
    });
    navigate(destinationHref, {
      state: { from: nextFrom },
    });
  };

  const contentRefOf = (content: Content): string =>
    typeof content.code === 'string' && content.code.trim().length > 0
      ? content.code
      : String(content.id);

  const handleAddAttachment = () => {
    if (attachSelection === null) return;
    setActionError(null);
    addAttachment.mutate(
      { contentId: parentContentId, payload: { childContentId: attachSelection } },
      {
        onSuccess: () => {
          setAttachPickerOpen(false);
          setAttachSelection(null);
        },
        onError: (error) => setActionError(getAxiosErrorDisplayMessage(error, 'Failed to attach content.')),
      }
    );
  };

  const handleCreateLink = () => {
    if (linkSelection === null) return;
    setActionError(null);
    createLink.mutate(
      { contentId: parentContentId, payload: { targetContentId: linkSelection } },
      {
        onSuccess: () => {
          setLinkPickerOpen(false);
          setLinkSelection(null);
        },
        onError: (error) => setActionError(getAxiosErrorDisplayMessage(error, 'Failed to link content.')),
      }
    );
  };

  const confirmRemove = () => {
    if (!removeTarget) return;
    setActionError(null);
    const onError = (error: unknown) =>
      setActionError(getAxiosErrorDisplayMessage(error, 'Failed to remove.'));
    if (removeTarget.kind === 'attachment') {
      removeAttachment.mutate(
        { contentId: parentContentId, childContentId: removeTarget.contentId },
        { onSuccess: () => setRemoveTarget(null), onError }
      );
    } else {
      deleteLink.mutate(
        { contentId: parentContentId, targetContentId: removeTarget.contentId },
        { onSuccess: () => setRemoveTarget(null), onError }
      );
    }
  };

  const listBody =
    items.length === 0 ? (
      <p className="text-muted-foreground text-sm py-2 px-4">{isLoading ? 'Loading…' : emptyText}</p>
    ) : (
      <ul data-testid="content-attachments-list" className="divide-y">
        {items.map((item) => {
          const contentItem = item as unknown as Content;
          const contentRef = contentRefOf(contentItem);
          const conversation =
            contentItem.contentType === ContentType.MESSAGE
              ? conversationByMessageId.get(contentItem.id)
              : undefined;
          const row =
            conversation ? (
              <ExploreConversationEntry
                conversation={conversation}
                checked={isItemSelected(contentItem.id)}
                onCheckedChange={() => toggleItem(contentItem.id)}
                onClick={() => {
                  navigateToAttachment(contentRef, contentItem);
                  clearSelectedItems();
                }}
              />
            ) : contentItem.contentType === ContentType.PHOTO ? (
              <PhotoEntry
                photo={contentItem}
                onClick={() => {
                  navigateToAttachment(contentRef, contentItem);
                  clearSelectedItems();
                }}
              />
            ) : contentItem.contentType === ContentType.RECORDING ? (
              <RecordingEntry
                recording={contentItem}
                onClick={() => {
                  navigateToAttachment(contentRef, contentItem);
                  clearSelectedItems();
                }}
              />
            ) : (
              <ListEntry
                content={contentItem}
                checked={isItemSelected(contentItem.id)}
                onCheckedChange={() => toggleItem(contentItem.id)}
                onClick={() => {
                  navigateToAttachment(contentRef, contentItem);
                  clearSelectedItems();
                }}
              />
            );
          if (!manageable) {
            return <li key={`${contentItem.contentType}-${contentItem.id}`}>{row}</li>;
          }
          return (
            <li
              key={`${contentItem.contentType}-${contentItem.id}`}
              className="flex items-center justify-between gap-2"
            >
              <div className="min-w-0 flex-1">{row}</div>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove attachment"
                className="mr-2 h-8 w-8 shrink-0"
                onClick={() =>
                  setRemoveTarget({
                    kind: 'attachment',
                    contentId: contentItem.id,
                    label: contentItem.title ?? `content #${contentItem.id}`,
                  })
                }
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          );
        })}
      </ul>
    );

  if (isEmbedded) {
    return <div className="flex flex-col border rounded-lg">{listBody}</div>;
  }

  const renderLinkRow = (content: Content, removable: boolean) => {
    const contentRef = contentRefOf(content);
    return (
      <li key={`link-${content.id}`} className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <ListEntry
            content={content}
            checked={isItemSelected(content.id)}
            onCheckedChange={() => toggleItem(content.id)}
            onClick={() => {
              navigateToAttachment(contentRef, content);
              clearSelectedItems();
            }}
          />
        </div>
        {removable && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Remove link"
            className="mr-2 h-8 w-8 shrink-0"
            onClick={() =>
              setRemoveTarget({
                kind: 'link',
                contentId: content.id,
                label: content.title ?? `content #${content.id}`,
              })
            }
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </li>
    );
  };

  return (
    <div className="flex flex-col gap-2 pt-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">{title}</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setActionError(null);
            setAttachSelection(null);
            setAttachPickerOpen(true);
          }}
        >
          <Plus className="mr-1 h-4 w-4" />
          Attach
        </Button>
      </div>
      <div className="flex flex-col gap-3 border rounded-lg">{listBody}</div>

      <div className="flex items-center justify-between pt-4">
        <h3 className="font-medium">Links</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setActionError(null);
            setLinkSelection(null);
            setLinkPickerOpen(true);
          }}
        >
          <Plus className="mr-1 h-4 w-4" />
          Link
        </Button>
      </div>
      <div className="flex flex-col gap-3 mb-36 border rounded-lg">
        {outgoingLinks.length === 0 && incomingLinks.length === 0 ? (
          <p className="text-muted-foreground text-sm py-2 px-4">
            {linksQuery.isLoading ? 'Loading…' : 'No links yet.'}
          </p>
        ) : (
          <ul className="divide-y">
            {outgoingLinks.map((content) => renderLinkRow(content as unknown as Content, true))}
            {incomingLinks.map((content) => renderLinkRow(content as unknown as Content, false))}
          </ul>
        )}
      </div>

      <Dialog
        open={attachPickerOpen}
        onOpenChange={(open) => {
          setAttachPickerOpen(open);
          if (!open) setActionError(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attach content</DialogTitle>
            <DialogDescription>
              Pick an existing content item to attach as a child.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <ItemSelector
              entityName="content"
              displayName="Content"
              items={toSelectorItems(pickerContent.data?.items ?? [])}
              selectedId={attachSelection}
              onSelect={setAttachSelection}
              loading={pickerContent.isLoading}
            />
            {actionError && <p className="mt-3 text-sm text-destructive">{actionError}</p>}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAttachPickerOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddAttachment}
              disabled={attachSelection === null || addAttachment.isPending}
            >
              {addAttachment.isPending ? 'Attaching…' : 'Attach'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={linkPickerOpen}
        onOpenChange={(open) => {
          setLinkPickerOpen(open);
          if (!open) setActionError(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link content</DialogTitle>
            <DialogDescription>Pick an existing content item to link to.</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <ItemSelector
              entityName="content"
              displayName="Content"
              items={toSelectorItems(pickerContent.data?.items ?? [])}
              selectedId={linkSelection}
              onSelect={setLinkSelection}
              loading={pickerContent.isLoading}
            />
            {actionError && <p className="mt-3 text-sm text-destructive">{actionError}</p>}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLinkPickerOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateLink}
              disabled={linkSelection === null || createLink.isPending}
            >
              {createLink.isPending ? 'Linking…' : 'Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRemoveTarget(null);
            setActionError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {removeTarget?.kind === 'link' ? 'Remove link' : 'Remove attachment'}
            </DialogTitle>
            <DialogDescription>
              {removeTarget?.kind === 'link'
                ? `Remove the link to '${removeTarget?.label}'? The content itself is not deleted.`
                : `Detach '${removeTarget?.label}'? The content itself is not deleted.`}
            </DialogDescription>
          </DialogHeader>
          {actionError && (
            <DialogBody>
              <p className="text-sm text-destructive">{actionError}</p>
            </DialogBody>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoveTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRemove}
              disabled={removeAttachment.isPending || deleteLink.isPending}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
